import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import * as Sentry from '@sentry/react';
import './matchMaking.css';
import MatchAudioPng from "utils/assets/images/Match-Audio.png";


const isTouchDevice = () => {
    try {
        return (('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0) ||
            (navigator.msMaxTouchPoints > 0));
    } catch (e) {
        Sentry.captureException(`562 Error in isTouchDevice: ${e}`);
        return false;
    }
};
// Shuffle function for DropTargets only
const shuffleArray = (array) => {
    let shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
    }
    return shuffledArray;
};

const ItemTypes = {
    MATCH_ITEM: "match_item",
};

const MatchItem = React.memo(({ id, image, isSelected, onReset, MatchType, audioMatch, onAudioPlay,dropContainerRef,isLength =false , text }) => {
    const [audio] = useState(new Audio(MatchType === 'ImageAndAudio' ? audioMatch : image));
    const [isPlaying, setIsPlaying] = useState(false);
    const dragRef = useRef(null);

    const scrollToTop = useCallback(() => {
        if (dropContainerRef?.current && isLength) {
            dropContainerRef.current.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [dropContainerRef, isLength]);


    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: ItemTypes.MATCH_ITEM,
        item: (monitor) => {
            scrollToTop();
            return { id, image, text, MatchType };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
            // canDrag: !isSelected && monitor.canDrag(),
        }),
        // canDrag: () => !isSelected,
        options: {
            touchSlop: 1, // Distance in pixels user must drag before triggering
            dragOffsetThreshold: 0, // Eliminates drag threshold
            anchorOffset: { x: -100, y: -100 }, // Precise cursor positioning
        },
        end: (item, monitor) => {
            if (!monitor.didDrop()) {
                 console.log('Drag cancelled');
            }
        },
    }));



    // const handleTouchMove = (e) => {
    //     if (!isDragging || !e.touches[0]) return;
        
    //     e.preventDefault();
    //     const touch = e.touches[0];
    //     updateTouchPosition(touch.clientY);
    //     startAutoScroll(); // Immediate activation
    // };


    useEffect(() => {
        try {
        const emptyImage = new Image();
        emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
        preview(emptyImage, { 
            captureDraggingState: true,
            anchorOffset: { x: 0, y: 0 }
        });
        } catch (error) {
            Sentry.captureException(`563 Error in preview setup: ${error}`);
            console.error('Preview setup failed:', error);
        }
    
        return () => {
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
            }
        };
    }, [preview, audio]);
    
    const onPlayAudio = () => {
        if (isPlaying) {
            audio.pause();
            audio.currentTime = 0;
            setIsPlaying(false);
        } else {
            audio.pause();
            audio.currentTime = 0;
            onAudioPlay(audio);
            audio.play();
            setIsPlaying(true);
        }
    };

    useEffect(() => {
        const handleAudioEnd = () => setIsPlaying(false);
        audio.addEventListener('ended', handleAudioEnd);
        return () => {
            audio.removeEventListener('ended', handleAudioEnd);
        };
    }, [audio]);

    const handlePlayAudio = (e) => {
        if (!isSelected && (MatchType === "Audio" || MatchType === "ImageAndAudio")) {
            onPlayAudio();
        }
    };

    return (
      <div
      className={`matchItemCstm2 ${MatchType === "Text" ? "" : "fixedWidthHeight"}`}
      ref={(node) => {
        drag(node);
        dragRef.current = node;
    }}
        style={{
          display: "flex",
          marginTop: "20px",
          flexWrap: "wrap",
          opacity: isSelected ? 0.2 : 1,
          cursor: isSelected ? "not-allowed" : "grab",
          marginRight: "20px",
          touchAction: isDragging ? "none" : "auto",
          userSelect: "none",
          WebkitUserSelect: "none",
          border: "1px solid gray",
          borderRadius: "5px",
          padding: "5px",
          boxShadow: isDragging ? "0 10px 24px rgba(0,0,0,0.22)" : "0 2px 4px rgba(0,0,0,0.2)",
          transform: isDragging ? "scale(1.03)" : "scale(1)",
          transition: "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease",
        }}
        draggable={"true"}
        onClick={() => {
            if(isSelected){
                onReset(id)
            }
            else{
                handlePlayAudio()
            }
        }}
      >
        <div>
          <div
            style={{
              borderRadius: "0px !important",
              minWidth: "20px",
              marginRight: "5px",
              maxHeight: "20px",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            className={`matchItemIndex`}
          >
            {String.fromCharCode(65 + id)}
          </div>
          {MatchType === "ImageAndAudio" && (
            <div
              style={{
                marginTop: "20px",
              }}
            >
              <img
                src={MatchAudioPng}
                alt={"audio"}
                loading="lazy"
                style={{
                  width: "23px",
                  height: "23px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            </div>
          )}
        </div>
        {MatchType === "Text" ? (
          <div style={{ 
            lineHeight: '1.6',
            wordBreak: 'break-word'
          }}>
            <style>{`
              div ol, div ul {
                padding-left: 30px;
                margin: 10px 0;
              }
              div ol {
                list-style-type: decimal;
              }
              div ul {
                list-style-type: disc;
              }
              div li {
                margin: 5px 0;
                line-height: 1.5;
              }
              div p {
                margin: 10px 0;
              }
            `}</style>
            <span dangerouslySetInnerHTML={{ __html: text || '' }} />
          </div>
        ) : (
          <img
            src={
              MatchType === "Audio"
                ? MatchAudioPng
                : image
            }
            alt={`Match ${id}`}
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
            loading="lazy"
            style={{
            //   width: "100px",
            //   height: "100px",
            //   objectFit: "contain",
              boxShadow: isDragging ? "0px 0px 5px rgba(0, 0, 0, 0.5)" : "none",
            }}
          />
        )}
      </div>
    );
});

const DropTarget = React.memo(({ id, onDrop, selectedItem, values, QuestionType, onAudioPlay }) => {
    const [audio] = useState(new Audio(QuestionType === 'Audio' ? values?.questionAudio : QuestionType === 'ImageAndAudio' ? values?.questionAudio : null));
    const [isPlaying, setIsPlaying] = useState(false);

    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.MATCH_ITEM,
        drop: (item) => onDrop(item.id, id),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
        canDrop: () => true, 
    }));

    useEffect(() => {
        if (!audio) return;
        const handleAudioEnd = () => setIsPlaying(false);
        audio.addEventListener('ended', handleAudioEnd);
        return () => {
            audio.removeEventListener('ended', handleAudioEnd);
            audio.pause();
            audio.currentTime = 0;
        };
    }, [audio]);

    const onPlayQuestionAudio = () => {
        if (!audio) return;
        
        if (isPlaying) {
            audio.pause();
            audio.currentTime = 0;
            setIsPlaying(false);
        } else {
            audio.pause();
            audio.currentTime = 0;
            onAudioPlay(audio);
            audio.play();
            setIsPlaying(true);
        }
    };

    const handlePlayAudio = () => {
        if (QuestionType === "Audio" || QuestionType === "ImageAndAudio") {
            onPlayQuestionAudio();
        }
    };

    return (
        <div
            ref={drop}
            style={{
                borderRadius: "0px !important",
                minWidth: "100px",
                height: "36px",
                marginRight: '5px',
                marginBottom: '15px',
                color: 'white',
                border: isOver && canDrop ? "1px dotted #000" : "1px dotted #000",
                transition: "background-color 0.3s, border 0.3s",
                background: isOver ? "#324b8c" : "linear-gradient(to right, #000000, #2b2bff)",
                cursor: (QuestionType === "Audio" || QuestionType === "ImageAndAudio") ? "pointer" : "default"
            }}
            className={`matchItem`}
            onClick={handlePlayAudio}>
            <div>
                {selectedItem !== undefined && (
                    <div
                        style={{
                            borderRadius: "0px !important",
                            minWidth: "18px",
                            marginRight: '5px',
                            maxHeight: '20px',
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                        className={`matchItemIndex`}
                    >
                        {String.fromCharCode(65 + selectedItem)}
                    </div>
                )}
            </div>
            <div className="match-making-student-match">
                {
                    QuestionType === 'Audio' ? <img src={MatchAudioPng} style={{width: '100px', height: '100px', objectFit: 'cover'}} alt="audio" /> : QuestionType === 'Image' ? <img src={values?.questionImage} style={{width: '100px', height: '100px', objectFit: 'cover'}} alt="image" /> : QuestionType === 'ImageAndAudio' ? <img src={values?.match_item} style={{width: '100px', height: '100px', objectFit: 'cover'}} alt="image" /> : (
                      <div style={{ 
                        lineHeight: '1.6',
                        wordBreak: 'break-word'
                      }}>
                        <style>{`
                         .match-making-student-match div ol, .matchItem div ul {
                            padding-left: 30px;
                            margin: 10px 0;
                          }
                          .match-making-student-match div ol {
                            list-style-type: decimal;
                          }
                         .match-making-student-match div ul {
                            list-style-type: disc;
                          }
                          .match-making-student-match div li {
                            margin: 5px 0;
                            line-height: 1.5;
                          }
                          .match-making-student-match div p {
                            margin: 10px 0;
                          }
                        `}</style>
                        <span dangerouslySetInnerHTML={{ __html: values?.item || '' }} />
                      </div>
                    )
                }
            </div>
        </div>
    );
});


const StudentMatch = ({ question, selectedAnswer, onClick }) => {
    
    const [selectedItems, setSelectedItems] = useState({});
    const [shuffledItems, setShuffledItems] = useState([]);
    const [matchItems, setMatchItems] = useState([]);
    const [windowHeight, setWindowHeight] = useState(() => {
        try {
            return window.innerHeight || 0;
        } catch (error) {
            return 0;
        }
    });
    const currentAudioRef = useRef(null);
    const dropContainerRef = useRef();

    const MatchType = question?.matchType || 'Image';
    const QuestionType = question?.questionType || 'Text';
    const dragEnabled = useRef(true);
    const is_touch = useMemo(() => isTouchDevice(), [])
    const shouldEnableLength = useMemo(() => {
        if (is_touch) {
            return true;
        }
        const matchTypeText = question?.matchType?.toLowerCase() === "text";
        return matchTypeText ? windowHeight < 1100 : true;
    }, [is_touch, windowHeight]);

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight || 0);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);


    useEffect(() => {
        if (selectedAnswer?.match_data) {
            const preselectedItems = {};
            selectedAnswer.match_data.forEach(({ item, match_item }) => {
                preselectedItems[item] = match_item;
            });
            setSelectedItems(preselectedItems);
        }
    }, [selectedAnswer]);

    useEffect(() => {
        dragEnabled.current = true;
        return () => {
            dragEnabled.current = false;
        };
    }, []);

    useEffect(() => {
        if (selectedAnswer?.match_data) {
            dragEnabled.current = true;
            const preselectedItems = {};
            selectedAnswer.match_data.forEach(({ item, match_item }) => {
                preselectedItems[item] = match_item;
            });
            setSelectedItems(preselectedItems);
        }
    }, [selectedAnswer]);

    

    // Shuffle the DropTarget items only (students will see shuffled options)
    useEffect(() => {
        const shuffledOptions = shuffleArray(question.options.map((option, index) => ({
            ...option,
            oldIndex: index, // Store the original index for correctness check
        })));
        setMatchItems(question.options); // Store the original items (used for the teacher)
        setShuffledItems(shuffledOptions); // Use shuffled items for the DropTarget display
    }, [question]);

    
    const onFinalClick = useCallback((selectedItemsMap) => {
        const finalData = Object.entries(selectedItemsMap).map(([targetId, itemId]) => {
            const isCorrect = matchItems[itemId]?.oldIndex === parseInt(targetId);
            return {
                item: parseInt(targetId),
                match_item: itemId,
                is_correct: isCorrect,
            };
        });
        onClick(finalData);
    }, [matchItems, onClick]);

    const handleDrop = useCallback((itemId, targetId) => {
        setSelectedItems((prev) => {
            const newSelected = { ...prev };
            
            // Find if the item is already selected somewhere
            const existingTarget = Object.entries(newSelected)
                .find(([_, value]) => value === itemId)?.[0];
    
            // If item is already selected and dropped in same target, do nothing
            if (existingTarget === targetId.toString()) {
                return prev;
            }
    
            // If item is already selected somewhere else, remove it first
            if (existingTarget) {
                delete newSelected[existingTarget];
            }
    
            // If target already has an item, remove it
            if (newSelected[targetId] !== undefined) {
                delete newSelected[targetId];
            }
    
            // Add new mapping
            newSelected[targetId] = itemId;
            onFinalClick(newSelected);
            return newSelected;
        });
    }, [onFinalClick]);


    const handleReset = (id) => {
        setSelectedItems((prev) => {
            const newSelected = { ...prev };
            for (let key in newSelected) {
                if (newSelected[key] === id) delete newSelected[key];
            }
            onFinalClick(newSelected);
            return newSelected;
        });
    };

    const handleAudioPlay = useCallback((newAudio) => {
        const currentAudio = currentAudioRef.current;
        if (currentAudio && currentAudio !== newAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
        }
        currentAudioRef.current = newAudio;
    }, []);

    const selectedItemSet = useMemo(() => new Set(Object.values(selectedItems)), [selectedItems]);

    return (
        <>
            <div ref={dropContainerRef}  >
                <div  style={{ display: "flex", flexWrap: "wrap", marginBottom: "20px" }}>
                    {shuffledItems.map((option, index) => {
                        const values = option.value || '';
                        const oldIndex = option?.oldIndex;
                        return (
                            <DropTarget
                                key={oldIndex}
                                id={oldIndex}
                                onDrop={handleDrop}
                                selectedItem={selectedItems[oldIndex]}
                                values={values}
                                QuestionType={QuestionType}
                                onAudioPlay={handleAudioPlay}
                            />
                        );
                    })}
                </div>
                <div className="matchPicCstm">
                    {matchItems &&matchItems.map((item, index) => (
                        <MatchItem
                            key={index}
                            id={index}
                            image={item.value?.match_item}
                            text={item.value?.match_text}
                            isSelected={selectedItemSet.has(index)}
                            onReset={handleReset}
                            MatchType={MatchType}
                            audioMatch={item.value?.match_audio || ""}
                            onAudioPlay={handleAudioPlay}
                            dropContainerRef={dropContainerRef}
                            isLength={shouldEnableLength}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};

export default StudentMatch;
